import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Loader2, Pencil, Plus, Scale, Trash2, Upload } from 'lucide-react';
import { Drawer } from '@components/Drawer';
import { Button } from '@components/Button';
import { ConfirmDialog } from '@components/Dialog';
import { ActionButton } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import accountService from '@services/accountService';
import categoryService from '@services/categoryService';
import type { Account, AccountBalance, Category, Statement, StatementLine, Transaction } from '@/types/finance';
import { ACCOUNT_TYPE_LABELS, canToggleSchedule } from '@/types/finance';
import { formatDateBR } from '@/utils/formatters';
import { formatBRL } from '@/utils/money';
import { currentMonth, monthRange, type DateRange } from '@/utils/period';
import { AmountText, BalanceText, ColorChip, PeriodPicker, ProjectedBadge, ScheduleBadge, ScheduleToggleButton } from './financeBits';
import { AccountFormModal } from './AccountFormModal';
import { BalanceAdjustModal } from './BalanceAdjustModal';
import { TransactionFormModal } from './TransactionFormModal';

interface Props {
  accountId: number;
  /** Contas ativas (para o formulário de lançamento/transferência). */
  accounts: Account[];
  onClose: () => void;
  /** Avisa a lista para recarregar (saldo mudou, conta editada). */
  onChanged: () => void;
  onError: (err: unknown) => void;
}

const Stat: React.FC<{ label: string; children: React.ReactNode; hint?: string }> = ({ label, children, hint }) => (
  <div className="min-w-0" title={hint}>
    <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
    <p className="text-sm font-medium">{children}</p>
  </div>
);

const lineDescription = (l: StatementLine): string => {
  if (l.kind === 'balance') return `Ajuste de saldo${l.balance?.notes ? ` · ${l.balance.notes}` : ''}`;
  const t = l.transaction;
  if (!t) return '';
  return t.description;
};

/**
 * Extrato da conta (GET /accounts/:id/statement): período (mês corrente por padrão), saldo
 * inicial, linhas com saldo corrido (ajuste de saldo destacado), saldo final e previsto; ajustes de
 * saldo (criar/excluir); atalhos "Novo lançamento" e "Importar extrato".
 */
export const AccountStatementDrawer: React.FC<Props> = ({ accountId, accounts, onClose, onChanged, onError }) => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financas.write').hasPermission;
  const canImport = checkPermission('financas.import').hasPermission;
  const [range, setRange] = useState<DateRange>(() => {
    const m = currentMonth();
    return monthRange(m.year, m.month);
  });
  const [statement, setStatement] = useState<Statement | null>(null);
  const [balances, setBalances] = useState<AccountBalance[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [newTx, setNewTx] = useState(false);
  const [editTx, setEditTx] = useState<Transaction | null>(null);
  const [deletingBalance, setDeletingBalance] = useState<AccountBalance | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!range.start || !range.end) return;
    setLoading(true);
    try {
      const [s, b] = await Promise.all([accountService.getStatement(accountId, range.start, range.end), accountService.getBalances(accountId)]);
      setStatement(s);
      setBalances(b);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, range.start, range.end]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canWrite) return;
    categoryService.getAll().then(setCategories).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canWrite]);

  const changed = () => {
    load();
    onChanged();
  };

  const confirmDeleteBalance = async () => {
    if (!deletingBalance) return;
    setBusy(true);
    try {
      await accountService.removeBalance(accountId, deletingBalance.id);
      setDeletingBalance(null);
      changed();
    } catch (err) {
      setDeletingBalance(null);
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const account = statement?.account;
  const modalOpen = editing || adjusting || newTx || !!editTx || !!deletingBalance;
  const lines = statement?.lines ?? [];
  /** Espelho de fatura de cartão não se edita pelo extrato (pague/edite pelo cartão). */
  const canEdit = (t?: Transaction) => !!t && canWrite && !t.credit_card_invoice_id;
  /** Agendamento marcado/desmarcado na linha: troca só o lançamento (saldo não muda). */
  const replaceTx = (updated: Transaction) =>
    setStatement((st) =>
      st ? { ...st, lines: st.lines.map((l) => (l.transaction?.id === updated.id ? { ...l, transaction: { ...l.transaction, ...updated } } : l)) } : st,
    );
  const extraBadges = (t?: Transaction) =>
    t ? (
      <>
        {t.credit_card_invoice_id ? (
          <span className="badge-warning ml-1" title="Pagamento de fatura de cartão: pague ou edite pelo cartão">
            Fatura
          </span>
        ) : null}
        {t.recurring_transaction_id ? (
          <span className="badge-info ml-1" title="Gerado por uma recorrência">
            Recorrente
          </span>
        ) : null}
        <ScheduleBadge status={t.schedule_status} className="ml-1" />
        {canWrite && canToggleSchedule(t) && (
          <span className="ml-0.5 align-middle">
            <ScheduleToggleButton transaction={t} onDone={replaceTx} onError={onError} />
          </span>
        )}
      </>
    ) : null;

  return (
    <>
      <Drawer
        isOpen
        onClose={onClose}
        closeOnEsc={!modalOpen}
        size="xl"
        title={account ? <ColorChip color={account.color}>{account.name}</ColorChip> : 'Conta'}
        headerActions={
          canWrite && account ? (
            <Button variant="outline" size="sm" icon={<Pencil />} onClick={() => setEditing(true)}>
              Editar
            </Button>
          ) : undefined
        }
      >
        {loading && !statement ? (
          <p className="text-center text-slate-400 py-10">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        ) : statement && account ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-small">
              <span>{ACCOUNT_TYPE_LABELS[account.account_type]}</span>
              {account.bank_name && (
                <span>
                  {account.bank_code ? `${account.bank_code} · ` : ''}
                  {account.bank_name}
                </span>
              )}
              {(account.branch || account.number) && <span>{[account.branch && `ag. ${account.branch}`, account.number && `c/c ${account.number}`].filter(Boolean).join(' · ')}</span>}
              {account.is_active === false && <span className="badge-neutral">Inativa</span>}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 card p-3">
              <Stat label="Saldo atual" hint="Até hoje (realizado)">
                <BalanceText cents={account.balance_cents} />
              </Stat>
              <Stat label="Saldo previsto" hint="Incluindo lançamentos futuros">
                <BalanceText cents={account.projected_cents} />
              </Stat>
              <Stat label="Entradas no período">
                <AmountText cents={Math.abs(statement.income_cents)} />
              </Stat>
              <Stat label="Saídas no período">
                <AmountText cents={-Math.abs(statement.expense_cents)} />
              </Stat>
            </div>

            <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
              {canWrite && (
                <>
                  <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setNewTx(true)}>
                    Novo lançamento
                  </Button>
                  <Button variant="outline" size="sm" icon={<Scale />} onClick={() => setAdjusting(true)}>
                    Ajustar saldo
                  </Button>
                </>
              )}
              {canImport && (
                <Link to={`/finance/import?account_id=${account.id}`} className="btn-outline text-sm px-3 py-1.5 inline-flex items-center gap-1.5 justify-center">
                  <Upload size={16} /> Importar extrato
                </Link>
              )}
            </div>

            {/* Extrato */}
            <section className="space-y-2">
              <PeriodPicker value={range} onChange={setRange} disabled={loading} />
              <div className={clsx('rounded-lg border border-slate-200 overflow-hidden', loading && 'opacity-60')}>
                <div className="flex flex-wrap items-center justify-between gap-x-2 px-3 py-2 bg-slate-50 text-sm">
                  <span className="text-slate-600">Saldo inicial em {formatDateBR(statement.start)}</span>
                  <BalanceText cents={statement.opening_balance_cents} className="font-medium" />
                </div>
                {lines.length === 0 ? (
                  <p className="text-center text-slate-500 py-8 text-sm">Nenhum lançamento no período.</p>
                ) : (
                  <>
                    {/* Celular: cartões */}
                    <ul className="md:hidden divide-y divide-slate-100">
                      {lines.map((l, i) => {
                        const t = l.transaction;
                        const isBalance = l.kind === 'balance';
                        return (
                          <li
                            key={`${l.kind}-${t?.id ?? l.balance?.id ?? i}`}
                            className={clsx('px-3 py-2 space-y-1', isBalance && 'bg-amber-50', canEdit(t) && 'cursor-pointer hover:bg-slate-50')}
                            onClick={() => canEdit(t) && t && setEditTx(t)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className={clsx('text-sm text-slate-800 [overflow-wrap:anywhere]', isBalance && 'font-medium text-amber-800')}>
                                  {lineDescription(l)}
                                  {t?.repeat_total && <span className="badge-neutral ml-1">{t.repeat_index}/{t.repeat_total}</span>}
                                  {extraBadges(t)}
                                </p>
                                <p className="text-small">
                                  {formatDateBR(l.date)}
                                  {t?.category_name && ` · ${t.category_name}`}
                                  {t?.is_projected && (
                                    <>
                                      {' '}
                                      <ProjectedBadge />
                                    </>
                                  )}
                                </p>
                              </div>
                              {isBalance ? <span className="badge-warning">Ajuste</span> : <AmountText cents={l.amount_cents} projected={t?.is_projected} className="text-sm" />}
                            </div>
                            <p className="text-small text-right">
                              Saldo: <BalanceText cents={l.balance_cents} />
                            </p>
                          </li>
                        );
                      })}
                    </ul>
                    {/* Tabela */}
                    <table className="hidden md:table w-full text-sm">
                      <thead className="table-header">
                        <tr>
                          <th className="py-2 px-3 text-left text-xs font-semibold text-slate-600 uppercase w-28">Data</th>
                          <th className="py-2 px-3 text-left text-xs font-semibold text-slate-600 uppercase">Descrição</th>
                          <th className="py-2 px-3 text-left text-xs font-semibold text-slate-600 uppercase">Categoria</th>
                          <th className="py-2 px-3 text-right text-xs font-semibold text-slate-600 uppercase">Valor</th>
                          <th className="py-2 px-3 text-right text-xs font-semibold text-slate-600 uppercase">Saldo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((l, i) => {
                          const t = l.transaction;
                          const isBalance = l.kind === 'balance';
                          return (
                            <tr
                              key={`${l.kind}-${t?.id ?? l.balance?.id ?? i}`}
                              className={clsx('border-t border-slate-100', isBalance ? 'bg-amber-50' : 'hover:bg-slate-50', canEdit(t) && 'cursor-pointer')}
                              onClick={() => canEdit(t) && t && setEditTx(t)}
                            >
                              <td className="py-2 px-3 whitespace-nowrap text-slate-600">{formatDateBR(l.date)}</td>
                              <td className={clsx('py-2 px-3 [overflow-wrap:anywhere]', isBalance && 'font-medium text-amber-800')}>
                                {lineDescription(l)}
                                {t?.repeat_total && <span className="badge-neutral ml-1">{t.repeat_index}/{t.repeat_total}</span>}
                                {extraBadges(t)}
                                {t?.is_projected && <ProjectedBadge className="ml-1" />}
                              </td>
                              <td className="py-2 px-3 text-slate-600">
                                {isBalance ? <span className="badge-warning">Ajuste de saldo</span> : t?.category_name ? <ColorChip color={t.category_color}>{t.category_name}</ColorChip> : <span className="text-slate-400">—</span>}
                              </td>
                              <td className="py-2 px-3 text-right">{isBalance ? <span className="text-slate-400">—</span> : <AmountText cents={l.amount_cents} projected={t?.is_projected} />}</td>
                              <td className="py-2 px-3 text-right">
                                <BalanceText cents={l.balance_cents} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                )}
                <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 text-sm space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-x-2">
                    <span className="text-slate-600">Saldo final em {formatDateBR(statement.end)}</span>
                    <BalanceText cents={statement.closing_balance_cents} className="font-semibold" />
                  </div>
                  {statement.projected_balance_cents !== statement.closing_balance_cents && (
                    <div className="flex flex-wrap items-center justify-between gap-x-2 text-small">
                      <span>Previsto (com lançamentos futuros)</span>
                      <BalanceText cents={statement.projected_balance_cents} className="italic" />
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Ajustes de saldo */}
            <section className="space-y-2">
              <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Scale size={16} className="text-slate-500" /> Ajustes de saldo
              </h3>
              {balances.length === 0 ? (
                <p className="text-small">
                  Nenhum ajuste registrado: o saldo é a soma de todos os lançamentos. Use "Ajustar saldo" para informar o saldo real do banco numa data.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                  {balances.map((b) => (
                    <li key={b.id} className="px-3 py-2 flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-800">
                          {formatDateBR(b.balance_date)} · <strong>{formatBRL(b.amount_cents)}</strong>
                        </p>
                        {b.notes && <p className="text-small [overflow-wrap:anywhere]">{b.notes}</p>}
                      </div>
                      {canWrite && <ActionButton icon={<Trash2 size={15} />} variant="delete" tooltip="Excluir ajuste" onClick={() => setDeletingBalance(b)} />}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        ) : null}
      </Drawer>

      {editing && account && (
        <AccountFormModal
          account={account}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            changed();
          }}
          onError={onError}
        />
      )}

      {adjusting && account && (
        <BalanceAdjustModal
          account={account}
          onClose={() => setAdjusting(false)}
          onSaved={() => {
            setAdjusting(false);
            changed();
          }}
          onError={onError}
        />
      )}

      {(newTx || editTx) && account && (
        <TransactionFormModal
          transaction={editTx}
          accounts={accounts.length > 0 ? accounts : [account]}
          categories={categories}
          prefillAccountId={account.id}
          onClose={() => {
            setNewTx(false);
            setEditTx(null);
          }}
          onSaved={() => {
            setNewTx(false);
            setEditTx(null);
            changed();
          }}
          onError={onError}
        />
      )}

      <ConfirmDialog
        isOpen={!!deletingBalance}
        onClose={() => setDeletingBalance(null)}
        onConfirm={confirmDeleteBalance}
        title="Excluir ajuste de saldo"
        message={`Excluir o ajuste de ${deletingBalance ? formatDateBR(deletingBalance.balance_date) : ''} (${deletingBalance ? formatBRL(deletingBalance.amount_cents) : ''})? O saldo volta a ser calculado pelos lançamentos (ou pelo ajuste anterior).`}
        confirmText="Excluir"
        severity="danger"
        loading={busy}
      />
    </>
  );
};
